import { useState } from 'react';

type Status = 'idle' | 'loading-lib' | 'rendering' | 'ready' | 'error';

interface BenchResult {
  libLoadMs: number;
  renderMs: number;
  totalMs: number;
  blobBytes: number;
}

export function SpikePage() {
  const [status, setStatus] = useState<Status>('idle');
  const [bench, setBench] = useState<BenchResult | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function runSpike(includeChart: boolean) {
    setStatus('loading-lib');
    setErrorMsg(null);
    setBench(null);
    setPdfUrl(null);
    const t0 = performance.now();

    try {
      const reactPdfModule = await import('@react-pdf/renderer');
      const { pdf } = reactPdfModule;
      const t1 = performance.now();

      const tplModule = includeChart
        ? await import('./ResponsePDFTemplateWithChart.spike')
        : await import('./ResponsePDFTemplate.spike');
      const sample = (await import('./ResponsePDFTemplate.spike')).SAMPLE_SPIKE_DATA;

      setStatus('rendering');
      const Component = (tplModule as { ResponsePDFTemplateSpike?: React.ComponentType<{ data: typeof sample }>; ResponsePDFTemplateWithChartSpike?: React.ComponentType<{ data: typeof sample }> });
      const Tpl = Component.ResponsePDFTemplateSpike || Component.ResponsePDFTemplateWithChartSpike;
      if (!Tpl) throw new Error('Template export not found');

      const t2 = performance.now();
      const blob = await pdf(<Tpl data={sample} />).toBlob();
      const t3 = performance.now();

      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      const result: BenchResult = {
        libLoadMs: Math.round(t1 - t0),
        renderMs: Math.round(t3 - t2),
        totalMs: Math.round(t3 - t0),
        blobBytes: blob.size,
      };
      setBench(result);
      setStatus('ready');

      console.log('[saas-1.19 spike] PDF generated', {
        ...result,
        blobKB: Math.round(blob.size / 1024),
        includeChart,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMsg(msg);
      setStatus('error');
      console.error('[saas-1.19 spike] Failed', err);
    }
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-white p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        <header>
          <h1 className="text-3xl font-bold">saas-1.19 — PDF v2 Spike</h1>
          <p className="text-neutral-400 mt-2">
            PoC: @react-pdf/renderer pipeline vetorial (lazy import via dynamic())
          </p>
        </header>

        <section className="bg-neutral-900 rounded-xl p-6 space-y-4">
          <h2 className="text-xl font-semibold">Generate PDF</h2>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => runSpike(false)}
              disabled={status === 'loading-lib' || status === 'rendering'}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-lg text-sm font-medium"
            >
              Generate response PDF
            </button>
            <button
              onClick={() => runSpike(true)}
              disabled={status === 'loading-lib' || status === 'rendering'}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 rounded-lg text-sm font-medium"
            >
              Generate w/ chart
            </button>
          </div>

          <div className="text-sm text-neutral-400">
            Status: <span className="text-white font-mono">{status}</span>
            {errorMsg && (
              <pre className="mt-2 text-red-400 text-xs whitespace-pre-wrap">{errorMsg}</pre>
            )}
          </div>
        </section>

        {bench && (
          <section className="bg-neutral-900 rounded-xl p-6 space-y-3">
            <h2 className="text-xl font-semibold">Bench Results</h2>
            <div className="grid grid-cols-2 gap-4 font-mono text-sm">
              <div>
                <div className="text-neutral-500 text-xs uppercase tracking-wider">Lib load</div>
                <div className="text-white text-lg">{bench.libLoadMs} ms</div>
              </div>
              <div>
                <div className="text-neutral-500 text-xs uppercase tracking-wider">Render</div>
                <div className="text-white text-lg">{bench.renderMs} ms</div>
              </div>
              <div>
                <div className="text-neutral-500 text-xs uppercase tracking-wider">Total</div>
                <div className="text-white text-lg">{bench.totalMs} ms</div>
              </div>
              <div>
                <div className="text-neutral-500 text-xs uppercase tracking-wider">Blob size</div>
                <div className="text-white text-lg">
                  {(bench.blobBytes / 1024).toFixed(1)} KB
                </div>
              </div>
            </div>
            <p className="text-xs text-neutral-500 pt-2 border-t border-neutral-800">
              p95 target (ADR-002 §5 trigger #1): &lt;2s single response. Result: {' '}
              {bench.totalMs < 2000 ? '✅ PASS' : '❌ FAIL'}
            </p>
          </section>
        )}

        {pdfUrl && (
          <section className="bg-neutral-900 rounded-xl p-6 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">PDF Preview</h2>
              <a
                href={pdfUrl}
                download="saas-1.19-spike.pdf"
                className="px-3 py-1.5 bg-green-600 hover:bg-green-500 rounded-lg text-sm font-medium"
              >
                Download
              </a>
            </div>
            <iframe
              src={pdfUrl}
              className="w-full h-[700px] rounded-lg border border-neutral-800 bg-white"
              title="PDF spike preview"
            />
            <p className="text-xs text-neutral-500">
              ✅ AC0.b: Texto vetorial selecionável — clique e arraste no PDF acima para selecionar.
              <br />
              ✅ AC0.c: Acentuação PT-BR — verifique &quot;ção&quot;, &quot;não&quot;, &quot;coração&quot;.
              <br />
              ✅ AC0.e: Lazy bundle — abra DevTools Network e veja chunk @react-pdf carregando só no
              click.
            </p>
          </section>
        )}

        <footer className="text-xs text-neutral-600 pt-4 border-t border-neutral-800">
          Spike route — `/spike/pdf-v2`. Não-prod. AC0.h dual gate: Aria + Pax #2.
        </footer>
      </div>
    </div>
  );
}
